import { Stars02 } from '@kula-ai/icons/dual-tone/Stars02';
import {
  interviewIntelligenceStatusMap,
  interviewIntelligenceMergedStatusMap,
  interviewIntelligenceReasonCopyMap,
} from '@/constants/interview-constants';
import { useHistory } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import {
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';

function getMergedStatus(beStatus: string) {
  return Object.values(interviewIntelligenceMergedStatusMap).find((group) =>
    group.keys.includes(beStatus)
  );
}

export const InterviewIntelligenceStatusTag = ({
  status,
  interviewId,
  reason,
}: {
  status: string;
  interviewId?: number;
  reason?: string | null;
}) => {
  const history = useHistory();
  const merged = getMergedStatus(status);
  const label = merged?.label ?? 'AI notetaker enabled';
  const { className = 'bg-pastel-3light text-pastel-3dark' } =
    interviewIntelligenceStatusMap[status] || {};

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interviewId) {
      history.push(`/interviews/${interviewId}`);
    }
  };

  const tag = (
    <Box
      display="inline-block"
      cursor={interviewId ? 'pointer' : 'default'}
      onClick={interviewId ? handleClick : undefined}
    >
      <Tag
        label={label}
        // `md` is the 14px/550 the badge has always rendered at; `sm` reads as a footnote.
        size="md"
        // The design-system default padding makes a noticeably fatter pill than the one this
        // badge replaces — match what KTagWithIcon rendered.
        padding="2px 8px"
        className={`whitespace-nowrap ${className}`}
        leftElement={<Stars02 size={16} />}
      />
    </Box>
  );

  // Reasons Product has not written copy for render no tooltip at all, rather than a generic string — ENG-3593.
  const tooltipCopy = reason
    ? interviewIntelligenceReasonCopyMap[reason]
    : undefined;

  if (!tooltipCopy) return tag;

  return (
    // `delayDuration={150}` — Radix's 700ms default reads as broken on a target this size (repo rule).
    // The span is the hover target: the tag is a Box that may carry its own click handler, and asChild
    // needs a single element that stays hoverable regardless.
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span style={{ display: 'inline-flex' }}>{tag}</span>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {tooltipCopy}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
